import React from "react";
import * as Icons from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FeatureCard } from "@/components/blocks/FeatureCard";
import { FAQ } from "@/components/blocks/FAQ";
import { CtaBand } from "@/components/blocks/CtaBand";
import { QuoteSection } from "@/components/blocks/QuoteSection";
import { Reveal } from "@/components/common/Reveal";
import { CampaignDashboard } from "@/components/visuals/CampaignDashboard";
import { contactLink } from "@/data/site";

const FEATURES = [
  { icon: "Contact", title: "Contact Management", desc: "Organise and manage your contact lists in one place." },
  { icon: "Users", title: "Audience Segmentation", desc: "Group audiences to send more relevant campaigns." },
  { icon: "Sparkles", title: "Personalized Campaigns", desc: "Personalise messages with names and dynamic fields." },
  { icon: "Image", title: "Media Messages", desc: "Send images, documents and rich media content." },
  { icon: "CalendarClock", title: "Campaign Scheduling", desc: "Schedule campaigns for the right time and timezone." },
  { icon: "FileText", title: "Template Management", desc: "Create and manage approved message templates." },
  { icon: "BarChart3", title: "Delivery Analytics", desc: "Track delivery, read and response performance." },
  { icon: "FileBarChart", title: "Campaign Reports", desc: "Get clear reports for every campaign you run." },
];

const FAQS = [
  { q: "Is bulk WhatsApp messaging allowed?", a: "Bulk WhatsApp campaigns must be permission-based and follow applicable laws, WhatsApp platform policies and consent requirements. E Telecom supports responsible, compliant messaging only." },
  { q: "Can I personalise campaigns?", a: "Yes. You can personalise messages using contact fields such as name, and segment your audience for more relevant communication." },
  { q: "Can I schedule campaigns?", a: "Campaigns can be scheduled in advance so messages reach recipients at the most appropriate time." },
  { q: "How do I measure results?", a: "You get delivery, read and response analytics along with per-campaign reports to understand performance." },
];

export default function BulkWhatsApp() {
  useSEO({
    title: "Bulk WhatsApp Campaigning & Marketing Services | E Telecom",
    description: "Run compliant, permission-based bulk WhatsApp campaigns with segmentation, personalization, scheduling and delivery analytics from E Telecom.",
    path: "/bulk-whatsapp-campaigning",
  });

  return (
    <>
      <PageHero
        eyebrow="Bulk WhatsApp Campaigning"
        icon="Send"
        title="Reach Your Customers on WhatsApp at Scale"
        description="Run targeted, permission-based WhatsApp campaigns for marketing, promotions and customer engagement — with clear analytics and reporting."
        primary={{ to: "/bulk-whatsapp#quote", label: "Request Pricing" }}
        secondary={{ to: contactLink("Bulk WhatsApp Campaigning"), label: "Talk to Sales" }}
        note="Campaigns should be sent only to recipients and audiences in accordance with applicable laws, platform policies and consent requirements."
        visual={<CampaignDashboard />}
      />

      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Capabilities" title="Everything you need to run compliant campaigns" subtitle="Plan, personalise and measure WhatsApp campaigns designed for responsible, permission-based communication." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => <FeatureCard key={f.title} icon={f.icon} title={f.title} description={f.desc} delay={(i % 4) * 0.05} />)}
          </div>
        </div>
      </section>

      {/* Compliance note */}
      <section className="et-section">
        <div className="et-container">
          <Reveal>
            <div className="et-card p-6 sm:p-8 border-l-4 border-l-[var(--et-primary)]">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)] shrink-0"><Icons.ShieldCheck className="h-5 w-5" /></span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">Responsible, permission-based messaging</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">E Telecom does not encourage spam or unsolicited messaging. Campaigns should be sent only to recipients and audiences who have provided consent, and in accordance with applicable laws, WhatsApp platform policies and opt-out requirements. Template approval and opt-out handling help keep your communication compliant.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <FAQ items={FAQS} title="Bulk WhatsApp FAQs" />
      <QuoteSection service="Bulk WhatsApp Campaigning" title="Plan Your WhatsApp Campaign" subtitle="Tell us about your audience and campaign goals — our team will share pricing and set you up for compliant, high-impact WhatsApp campaigns." testIdPrefix="bulkwa-quote" />
      <CtaBand title="Plan your next WhatsApp campaign" subtitle="Our team will help you set up compliant, high-impact WhatsApp campaigns for your audience." primary={{ to: "/bulk-whatsapp#quote", label: "Request Pricing" }} secondary={{ to: contactLink("Bulk WhatsApp Campaigning"), label: "Talk to Sales" }} />
    </>
  );
}
